import { Line, LineOptions } from '@antv/g2plot'
import dayjs from 'dayjs'
import { TFunction } from 'i18next'
import escape from 'lodash/escape'
import { useEffect, useMemo, useRef } from 'react'

import { MonitorChartItem } from '@/api/type-response'
import { Trans } from '@/components'
import { useThemeMode } from '@/contexts/ThemeContext'
import { ChartType, enumToChartType, extractTimePoints } from '@/models/chart'
import { Instance } from '@/models/instance'
import { getChartThemeColors, getLineChartAxisTheme } from '@/utils/chart-theme'
import { mb2GbString } from '@/utils/file'
import { getCssVarRgb } from '@/utils/theme-color'

export interface MonitorItemData {
  id: string
  name: string
  gpuIndex?: number
  timeMin?: number
  timeMax?: number
  type: ChartType
  title: React.ReactElement
  description: React.ReactElement
  realtime: React.ReactElement
  values: MonitorValue[]
  isEmpty: boolean
  yMax?: number
}

interface MonitorValue {
  time: string
  value: number | undefined
  unit: string
}

// Title
function getChartTitle(t: TFunction, chartType: ChartType, vcpuCount = 1): string[] {
  const nameMap = {
    [ChartType.CPU]: [
      t('instance.detail.monitor.cpuUsage'),
      t('instance.detail.monitor.cpuSumSuffix', { count: vcpuCount }),
    ],
    [ChartType.Memory]: [t('instance.detail.monitor.memoryUsage')],
    [ChartType.Disk]: [t('instance.detail.monitor.diskUsage')],
    [ChartType.Bandwidth]: [t('instance.detail.monitor.bandwidth')],
    [ChartType.GPUCore]: [t('instance.detail.monitor.gpuCore')],
    [ChartType.GPURAM]: [t('instance.detail.monitor.gpuRam')],
  } as any
  return nameMap[chartType] || [t('instance.detail.monitor.unknown')]
}

const renderChartTitle = (name: string, suffix: string, type: ChartType) => (
  <div className="flex flex-wrap items-baseline gap-x-2">
    <span className="t-sm font-bold text-primary">{name}</span>
    {suffix && <span className="t-xs text-tertiary">{suffix}</span>}
    {[ChartType.Memory, ChartType.Disk, ChartType.GPURAM].includes(type) && (
      <span className="t-xs text-secondary">(GB)</span>
    )}
  </div>
)

// Desc
function getChartDescription(instance: Instance, chartType: ChartType): string[] {
  const nameMap = {
    [ChartType.CPU]: [`${instance.hardware.machine.cpuName}`],
    [ChartType.Memory]: [],
    [ChartType.Disk]: [],
    [ChartType.Bandwidth]: [],
    [ChartType.GPUCore]: [`${instance.hardware.gpu.gpuName}`],
    [ChartType.GPURAM]: [instance.hardware.gpu.gpuName],
  } as any
  return nameMap[chartType] || ['']
}

const renderChartDescription = (instance: Instance, type: ChartType) => {
  const descs = getChartDescription(instance, type)
  return (
    <div>
      <div className="t-xs font-medium text-secondary truncate" title={descs[0]}>
        {descs[0]}
      </div>
    </div>
  )
}

// Realtime
function getChartRealtime(
  instance: Instance,
  chartType: ChartType,
  item: MonitorValue,
  gpuCapacityMB?: number,
): string[] {
  const vcpuCount = instance.hardware.machine.cpuIds?.length || 1
  const nameMap = {
    [ChartType.CPU]: [`${item.value || 0}%`, `/${vcpuCount * 100}%`],
    [ChartType.Memory]: [
      `${convertToGB(item.value || 0, item.unit)}`,
      `/${mb2GbString(instance.hardware.machine.ramMB)}`,
    ],
    [ChartType.Disk]: [
      `${convertToGB(item.value || 0, item.unit)}`,
      `/${instance.hardware.machine.diskGB}GB`,
    ],
    [ChartType.Bandwidth]: [`${item.value}`],
    [ChartType.GPUCore]: [`${item.value || 0}%`],
    [ChartType.GPURAM]: [
      `${convertToGB(item.value || 0, item.unit)}${gpuCapacityMB ? '' : ' GB'}`,
      gpuCapacityMB ? `/${mb2GbString(gpuCapacityMB)}` : '',
    ],
  } as any
  return nameMap[chartType] || ['']
}

const renderChartRealtime = (
  instance: Instance,
  type: ChartType,
  item: MonitorValue,
  gpuCapacityMB?: number,
) => {
  const descs = item.value == null ? ['—'] : getChartRealtime(instance, type, item, gpuCapacityMB)
  return (
    <div className="bg-panel-gray-100 flex shrink-0 items-center py-2 px-3 t-bold whitespace-nowrap tabular-nums">
      <span className="text-primary">{descs[0]}</span>
      <span className="text-tertiary">{descs[1]}</span>
    </div>
  )
}

export const buildChartItemData = (
  t: TFunction,
  instance: Instance,
  item: MonitorChartItem,
  timePoints: dayjs.Dayjs[],
  gpuIndex?: number,
  gpuCapacityMB?: number,
): MonitorItemData => {
  const chartType = enumToChartType(item.chartType)
  const data = (item.result[0]?.matrixArray || []).map(({ matrix }) => ({
    time: dayjs.unix(matrix[0]),
    value: matrix[1],
  }))
  const max = data.reduce((max, point) => Math.max(max, point.value ?? 0), 0)
  const fmax = formatValue(chartType, max)
  const isEmpty = data.every((point) => point.value == null)
  const values = (data.length ? data : timePoints.map((time) => ({ time, value: undefined }))).map(
    (point) => convertToMonitorValue(point, fmax),
  )
  const lastData = data[data.length - 1]
  const lastValue = lastData
    ? convertToMonitorValue(lastData, fmax)
    : { time: '', value: undefined, unit: fmax.unit }
  const vcpuCount = instance.hardware.machine.cpuIds?.length || 1
  const titles = getChartTitle(t, chartType, vcpuCount)
  const name = `${titles[0]}${gpuIndex == null ? '' : `-${String(gpuIndex + 1).padStart(2, '0')}`}`
  return {
    id: gpuIndex == null ? String(chartType) : `${chartType}-${gpuIndex}`,
    name,
    gpuIndex,
    timeMin: timePoints[0]?.valueOf(),
    timeMax: timePoints[timePoints.length - 1]?.valueOf(),
    title: renderChartTitle(name, titles[1], chartType),
    description: renderChartDescription(instance, chartType),
    realtime: renderChartRealtime(instance, chartType, lastValue, gpuCapacityMB),
    type: chartType,
    values,
    isEmpty,
    yMax:
      chartType === ChartType.GPURAM && gpuCapacityMB
        ? Math.max(gpuCapacityMB / 1024, max / 1024 ** 3)
        : getChartYMax(chartType, vcpuCount),
  }
}

export function buildMonitorChartData(
  t: TFunction,
  instance: Instance,
  items: MonitorChartItem[],
): MonitorItemData[] {
  const timePoints = extractTimePoints(items)
  const charts: MonitorItemData[] = []
  const core = items.find((item) => item.chartType === ChartType.GPUCore)
  const ram = items.find((item) => item.chartType === ChartType.GPURAM)
  const count = Math.max(core?.result.length || 0, ram?.result.length || 0, core || ram ? 1 : 0)
  const hardwareCount = instance.hardware.gpu.gpuIds?.length || 0
  // gpuRamMB is the instance total. Historical/unnamed sequences may not match its current GPUs.
  const gpuCapacityMB =
    hardwareCount > 0 && hardwareCount === count && instance.hardware.gpu.gpuRamMB > 0
      ? instance.hardware.gpu.gpuRamMB / hardwareCount
      : undefined
  for (let index = 0; index < count; index++) {
    for (const type of [ChartType.GPUCore, ChartType.GPURAM]) {
      const result = (type === ChartType.GPUCore ? core : ram)?.result[index]
      charts.push(
        buildChartItemData(
          t,
          instance,
          { chartType: type, result: result ? [result] : [] },
          timePoints,
          index,
          gpuCapacityMB,
        ),
      )
    }
  }
  charts.push(
    ...[ChartType.CPU, ChartType.Memory, ChartType.Disk, ChartType.Bandwidth]
      .flatMap((type) => items.filter((item) => item.chartType === type))
      .map((item) => buildChartItemData(t, instance, item, timePoints)),
  )
  return charts
}

function getChartYMax(chartType: ChartType, vcpuCount: number): number | undefined {
  if (chartType === ChartType.CPU) return vcpuCount * 100
  if (chartType === ChartType.GPUCore) return 100
  return undefined
}

function convertToMonitorValue(
  v: { time: dayjs.Dayjs; value?: number },
  fmax: { value: number; unit: string },
): MonitorValue {
  return {
    time: v.time.format(),
    value: v.value == null ? undefined : formatValueV2(v.value, fmax.unit),
    unit: fmax.unit,
  }
}

function formatValueV2(value: number, unit: string) {
  if (unit == '%') return parseFloat(value.toFixed(2))
  if (unit == 'B') return value
  if (unit == 'KB') return parseFloat((value / 1024).toFixed(2))
  if (unit == 'MB') return parseFloat((value / 1024 / 1024).toFixed(2))
  if (unit == 'GB') return parseFloat((value / 1024 / 1024 / 1024).toFixed(2))
  if (unit == 'TB') return parseFloat((value / 1024 / 1024 / 1024 / 1024).toFixed(2))
}

function convertToGB(value: number, unit: string) {
  if (unit == 'B') return parseFloat((value / 1024 / 1024 / 1024).toFixed(2))
  if (unit == 'KB') return parseFloat((value / 1024 / 1024).toFixed(2))
  if (unit == 'MB') return parseFloat((value / 1024).toFixed(2))
  if (unit == 'GB') return value
  if (unit == 'TB') return value * 1024
}

function formatValue(chartType: ChartType, value: number): { value: number; unit: string } {
  switch (chartType) {
    case ChartType.CPU:
      return { value: value, unit: '%' }
    case ChartType.Memory:
    case ChartType.Disk:
    case ChartType.GPURAM:
      return { value: value / 1024 ** 3, unit: 'GB' }
    case ChartType.Bandwidth:
      return { value: value, unit: 'G' }
    case ChartType.GPUCore:
      return { value: value, unit: '%' }
    default:
      return bytesToSize(value)
  }
}

function bytesToSize(bytes: number) {
  if (bytes === 0) return { value: 0, unit: 'B' }
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return {
    value: parseFloat((bytes / Math.pow(k, i)).toFixed(2)),
    unit: sizes[i],
  }
}

export function Chart(props: { value: MonitorItemData }) {
  const { themeMode } = useThemeMode()
  const id = useMemo(() => Math.random().toString(36).replace('0.', 'ID_'), [])
  const lineRef = useRef<Line>()
  const optionsRef = useRef<LineOptions>()
  const { name, isEmpty, yMax, values, timeMin, timeMax } = props.value
  const unit = values[0]?.unit || ''

  const options = useMemo<LineOptions>(() => {
    const usage = unit == '%'
    const chartTheme = getChartThemeColors()
    const axisTheme = getLineChartAxisTheme(themeMode, isEmpty, {
      gridColor: chartTheme.grid,
    })
    const chartFill = `l(270) 0:${getCssVarRgb('--color-highlight', '#365FEF', 0)} 1:${getCssVarRgb(
      '--color-highlight',
      '#365FEF',
      0.5,
    )}`

    return {
      data: [],
      animation: false,
      padding: 'auto',
      xField: 'time',
      yField: 'value',
      color: chartTheme.highlight,
      legend: false,
      meta: { time: { type: 'time', mask: 'HH:mm', nice: false, min: timeMin, max: timeMax } },

      xAxis: {
        tickCount: 7,
        tickLine: {
          length: -5,
        },
        line: axisTheme.axisLine,
        grid: axisTheme.xGrid,
      },
      yAxis: {
        tickCount: usage ? 6 : undefined,
        label: {
          formatter: (v) => {
            return usage ? `${v}%` : v
          },
        },
        min: 0,
        max: yMax ?? (usage ? 100 : undefined),
        grid: axisTheme.yGrid,
      },
      area: { style: { fill: chartFill } },
      tooltip: {
        showMarkers: false,
        shared: true,
        title: (_title, datum) => dayjs(datum.time).format('YYYY-MM-DD HH:mm:ss'),
        domStyles: {
          'g2-tooltip': {
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            color: 'white',
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: '12px',
            lineHeight: '16px',
            fontWeight: '500',
            textAlign: 'left',
          },
        },
        showCrosshairs: true,
        crosshairs: {
          line: {
            style: {
              lineWidth: 1,
              lineDash: [5, 3],
              color: chartTheme.crosshair,
            },
          },
        },
        formatter: (datum) => {
          return {
            name: escape(name),
            value: datum.value == null ? '—' : `${datum.value}${unit}`,
          }
        },
      },
      smooth: true,
      lineStyle: {
        lineWidth: 1,
      },
    }
  }, [name, isEmpty, yMax, unit, timeMin, timeMax, themeMode])

  useEffect(() => {
    if (!lineRef.current) {
      lineRef.current = new Line(id, { ...options, data: values })
      lineRef.current.render()
    } else if (optionsRef.current !== options) {
      lineRef.current.update({ ...options, data: values })
    } else {
      lineRef.current.changeData(values)
    }
    optionsRef.current = options
  }, [id, options, values])

  useEffect(() => {
    return () => {
      lineRef.current?.destroy()
      lineRef.current = undefined
      optionsRef.current = undefined
    }
  }, [])

  return (
    <div className="h-[276px] relative">
      <div
        id={id}
        className="h-full w-full overflow-hidden text-center text-tertiary t-sm absolute"
      ></div>
      {props.value?.isEmpty && (
        <div className="absolute h-full w-full flex items-center justify-center">
          <Trans i18nKey="common.emptyViewNoDataMsg">No Data</Trans>
        </div>
      )}
    </div>
  )
}
